import Promise from 'bluebird'
import CreateReducer from '../utils/createReducer'
import CreateActions from '../utils/createActions'

import calendarUtils from 'shared/src/calendar'

import {
  handlers,
  getErrorMessage,
} from '../utils/api'

import networkWrapper from '../utils/networkWrapper'
import projectActions from './project'
import bookingformActions from './bookingform'
import routerActions from './router'
import routerSelectors from '../selectors/router'
import bookingSelectors from '../selectors/booking'
import authSelectors from '../selectors/auth'
import snackbarActions from './snackbar'
import {
  getReservationPaymentStatus,
} from '../../utils/reservation'

const prefix = 'booking'
const wrapper = networkWrapper.factory(prefix)

const initialState = {
  calendar: [],
  config: null,
  condensedDates: [],
  currentBookingform: null,
  slot: null,
  options: null,
  details: null,
  reservation: null,
  reservationRedirect: null,
  recaptchaKey: '',
  reservedBookingToken: '',
  stripeBookingToken: '',
  reservationError: '',
  embedHTML: '',
}

const reducers = {
  setCalendar: (state, action) => {
    state.calendar = action.payload
  },
  setConfig: (state, action) => {
    state.config = action.payload
  },
  setCondensedDates: (state, action) => {
    state.condensedDates = action.payload
  },
  setCurrentBookingform: (state, action) => {
    state.currentBookingform = action.payload
  },
  setSlot: (state, action) => {
    state.slot = action.payload
  },
  setOptions: (state, action) => {
    state.options = action.payload
  },
  setDetails: (state, action) => {
    state.details = action.payload
  },
  setReservation: (state, action) => {
    state.reservation = action.payload
  },
  setReservationRedirect: (state, action) => {
    state.reservationRedirect = action.payload
  },
  setRecaptchaKey: (state, action) => {
    state.recaptchaKey = action.payload
  },
  setReservedBookingToken: (state, action) => {
    state.reservedBookingToken = action.payload
    localStorage.setItem('reservedBookingToken', action.payload)
  },
  setStripeBookingToken: (state, action) => {
    state.stripeBookingToken = action.payload
  },
  setReservationError: (state, action) => {
    let error = action.payload
    if(error == 'timeout') error = 'The booking reservation has timed out - please restart your booking'
    state.reservationError = error
  },
  setEmbedHTML: (state, action) => {
    state.embedHTML = action.payload
  }
}

const sideEffects = {

  reloadReservedBooking: () => wrapper('reloadReservedBooking', async (dispatch, getState) => {
    const reservedBookingToken = bookingSelectors.reservedBookingToken(getState())
    const stripeBookingToken = bookingSelectors.stripeBookingToken(getState())
    const reservation = await dispatch(actions.reservationApiWrapper(handlers.get(`/booking/reservation`, null, {
      params: {
        token: reservedBookingToken,
        stripe_token: stripeBookingToken,
      }
    })))
    if(!reservation) return
    const {
      booking,
      slot,
    } = reservation
    const {
      data: {
        options,
        details,
      }
    } = booking
    dispatch(actions.setReservation(reservation.booking))
    dispatch(actions.setReservedBookingToken(reservedBookingToken))
    dispatch(actions.setSlot(slot))
    dispatch(actions.setOptions(options))
    dispatch(actions.setDetails(details))
  }, {
    globalLoading: false,
  }),

  initialiseReservedBookingToken: (reservedBookingToken, stripeBookingToken) => wrapper('initialiseLocalBookingToken', async (dispatch, getState) => {
    const reservation = await dispatch(actions.reservationApiWrapper(handlers.get(`/booking/reservation`, null, {
      params: {
        token: reservedBookingToken,
        stripe_token: stripeBookingToken,
      }
    })))
    if(!reservation) return
    const {
      projectUrl,
      bookingformUrl,
      booking,
      slot,
    } = reservation
    const {
      project,
      bookingform,
      data: {
        options,
        details,
      }
    } = booking
    await dispatch(actions.loadCalendar({
      project,
      bookingform,
      date: slot.date,
      days: 3,
    }))
    dispatch(actions.setReservation(reservation.booking))
    dispatch(actions.setReservedBookingToken(reservedBookingToken))
    dispatch(actions.setSlot(slot))
    dispatch(actions.setOptions(options))
    dispatch(actions.setDetails(details))
    return {
      projectUrl,
      project,
      reservation: booking,
      bookingformUrl,
      bookingform,
    }
  }, {
    globalLoading: false,
  }),

  // called by the public booking Layout wrapper
  initialise: () => wrapper('initialise', async (dispatch, getState) => {
    const urlParams = new URLSearchParams(window.location.search)
    const stripeBookingToken = urlParams.get('stripe_bookingtoken')
    const stripeCancelled = urlParams.get('stripe_cancelled')
    const reservedBookingToken = localStorage.getItem('reservedBookingToken')
    if(!reservedBookingToken) return
    dispatch(actions.setStripeBookingToken(stripeBookingToken))
    const result = await dispatch(actions.initialiseReservedBookingToken(reservedBookingToken, stripeBookingToken))
    if(!result) return
    const {
      projectUrl,
      project,
      bookingformUrl,
      bookingform,
      reservation,
    } = result

    const paymentStatus = getReservationPaymentStatus(reservation)

    let page = 'customize'
    if(stripeCancelled) page = 'pay'
    else if(paymentStatus == 'waiting' || paymentStatus == 'confirmed') {
      page = 'confirm'
    }

    const reservationRedirect = {
      type: 'redirect',
      name: `book.form.${page}`,
      project,
      projectUrl,
      bookingform,
      bookingformUrl,
      params: {
        project: projectUrl || project,
        bookingform: bookingformUrl || bookingform,
        reservedBookingToken,
        stripeBookingToken,
      }
    }

    dispatch(actions.setReservationRedirect(reservationRedirect))
    return reservationRedirect
  }, {
    globalLoading: false,
  }),

  reset: () => wrapper('reset', async (dispatch, getState) => {
    dispatch(actions.setReservedBookingToken(''))
    dispatch(actions.setSlot(null))
    dispatch(actions.setOptions(null))
    dispatch(actions.setDetails(null))
    dispatch(actions.setReservationError(''))
  }, {
    globalLoading: false,
  }),

  clearReservationError: () => wrapper('clearReservationError', async (dispatch, getState) => {
    dispatch(actions.reset())
    const params = routerSelectors.params(getState())
    if(params.project && params.bookingform) {
      dispatch(routerActions.navigateTo('book.form.calendar', {
        project: params.project,
        bookingform: params.bookingform,
        date: 'today',
      }))
    }
    else {
      dispatch(routerActions.navigateTo('home'))
    }
  }, {
    globalLoading: false,
  }),

  reservationApiWrapper: (handler) => wrapper('reservationApiWrapper', async (dispatch, getState) => {
    try {
      const data = await handler
      if(data.error) {
        console.error(data.error)
        dispatch(actions.setReservationError(data.error))
        return null
      }
      return data
    } catch(e) {
      console.error(e)
      let errorMessage = getErrorMessage(e)
      if(e.response && e.response.status == 401) {
        errorMessage = `Your booking reservation was not found`
      }
      dispatch(actions.setReservationError(errorMessage))
      return null
    }
  }, {
    globalLoading: false,
  }),

  loadProject: ({
    id,
  }) => wrapper('loadProject', async (dispatch, getState) => {
    const result = await handlers.get(`/booking/project/${id}`)
    dispatch(projectActions.setProject(result.project))
    dispatch(bookingformActions.setBookingForms(result.bookingForms))
  }, {
    globalLoading: false,
  }),

  loadCalendar: ({
    project,
    bookingform,
    date,
    condensed,
    days = 3,
    direction = 1,
    admin,
  }) => wrapper('loadCalendar', async (dispatch, getState) => {
    const data = await handlers.get(`/booking/calendar/${project}/${bookingform}/${date}`, null, {
      params: {
        days,
        condensed: condensed ? 'true' : 'false',
        direction,
        admin: admin ? 'true' : 'false',
      },
    })
    const calendar = calendarUtils.explodeCalendar(data.calendar, data.slotCache)
    dispatch(actions.setCalendar(calendar))
    dispatch(actions.setCurrentBookingform(data.bookingform))
    dispatch(actions.setCondensedDates(data.condensedDates))
    return calendar
  }, {
    globalLoading: false,
  }),

  loadEmbedHTML: ({
    project,
    bookingform,
    days = 3,
    slotsPerDay = 1,
  }) => wrapper('loadEmbedHTML', async (dispatch, getState) => {
    const data = await handlers.get(`/booking/embedHTML/${project}/${bookingform}`, null, {
      params: {
        days,
        slotsPerDay,
      },
    })
    dispatch(actions.setEmbedHTML(data.html))
  }, {
    globalLoading: false,
  }),

  loadCalendarConfig: ({
    project,
    bookingform,
  }) => wrapper('loadCalendarConfig', async (dispatch, getState) => {
    const data = await handlers.get(`/booking/config/${project}/${bookingform}`)    
    dispatch(actions.setConfig(data))
    return data
  }, {
    globalLoading: false,
  }),

  loadRecaptchaKey: () => wrapper('loadRecaptchaKey', async (dispatch, getState) => {
    const {
      key,
    } = await handlers.get(`/booking/recaptcha_key`)
    dispatch(actions.setRecaptchaKey(key))
  }, {
    globalLoading: false,
  }),

  reserve: ({
    project,
    bookingform,
    slot,
    recaptchaToken,
  }) => wrapper('reserve', async (dispatch, getState) => {
    try {
      const existingReservationToken = bookingSelectors.reservedBookingToken(getState())
      const payload = {
        date: slot.date,
        block_id: slot.block_id,
        slot_id: slot.id,
        recaptchaToken,
        existingReservationToken,
      }
      const result = await handlers.post(`/booking/reserve/${project}/${bookingform}`, payload)
      dispatch(actions.setReservation(result.booking))
      dispatch(actions.setReservedBookingToken(result.token))
      dispatch(actions.setSlot(slot))
      dispatch(routerActions.navigateTo('book.form.customize', {
        project,
        bookingform,
      }))
      return result
    } catch(e) {
      console.error(e.response.data)
      throw new Error(e.response.data.error)
    }
  }),

  updateReservation: (data) => wrapper('updateReservation', async (dispatch, getState) => {
    const reservedBookingToken = bookingSelectors.reservedBookingToken(getState())
    if(reservedBookingToken) {
      const reservation = await dispatch(actions.reservationApiWrapper(handlers.put(`/booking/reservation`, data, {
        params: {
          token: reservedBookingToken,
        }
      })))
      if(!reservation) return
    }
    return true
  }),

  cancelBooking: () => wrapper('cancelBooking', async (dispatch, getState) => {
    const reservedBookingToken = bookingSelectors.reservedBookingToken(getState())
    if(reservedBookingToken) {
      try {
        await handlers.delete(`/booking/reservation`, null, {
          params: {
            token: reservedBookingToken,
          }
        })
        dispatch(actions.reset())
        dispatch(snackbarActions.setSuccess(`your booking has been cancelled`))
        
      }
      catch(e) {

      }
    }
    return true
  }),

  pay: ({
    project,
    bookingform,
  }) => wrapper('pay', async (dispatch, getState) => {
    const reservedBookingToken = bookingSelectors.reservedBookingToken(getState())
    if(!reservedBookingToken) {
      dispatch(actions.reset())
      dispatch(routerActions.navigateTo('book.form.calendar', {
        project,
        bookingform,
        date: 'today',
      }))
      throw new Error(`no booking session was found`)
    }
  
    const result = await dispatch(actions.reservationApiWrapper(handlers.post(`/booking/pay`, {
      // IMPORTANT don't include existing query params in the URL
      url: document.location.href.split('?')[0],
    }, {
      params: {
        token: reservedBookingToken,
      }
    })))

    if(!result) return

    if(!result.stripeSessionId) throw new Error(`no stripe session found`)
    if(!result.stripePublicKey) throw new Error(`no stripe public key found`)
    if(!result.destinationStripeAccount) throw new Error(`no stripe destination id found`)

    const stripe = Stripe(result.stripePublicKey, {
      stripeAccount: result.destinationStripeAccount,
    })
    const {error} = await stripe.redirectToCheckout({
      sessionId: result.stripeSessionId,
    })
    if(error) throw new Error(error)
  }, {
    globalLoading: true,
  }),

}

const reducer = CreateReducer({
  initialState,
  reducers,
  prefix,
})

const actions = CreateActions({
  reducers,
  sideEffects,
  prefix,
})

export { actions, reducer }
export default actions